import { useEffect, useState } from "react";
import { DeviceMobile, Desktop } from "@phosphor-icons/react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const SESSION_KEY = "mrd_mobile_advisory_ack";
const MOBILE_BREAKPOINT = 768; // Tailwind md

/**
 * MobileAdvisory
 * A one-time-per-session courtesy notice shown on the Builder page when the
 * user is on a small viewport (< md / < 768 px). Copy is deliberately
 * professional — it neither blocks the experience nor implies the mobile flow
 * is unsupported.
 *
 * The parent decides *when* to render this (e.g. after the primary Privacy
 * modal is acknowledged) by toggling the `open` prop.
 */
export default function MobileAdvisory({ open, onClose }) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    // Modern browsers: addEventListener; older: addListener
    if (mql.addEventListener) mql.addEventListener("change", handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", handler);
      else mql.removeListener(handler);
    };
  }, []);

  const shouldShow = open && isMobile;

  const acknowledge = () => {
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch (e) {
      /* ignore */
    }
    onClose?.();
  };

  if (!shouldShow) return null;

  return (
    <AlertDialog open={true} onOpenChange={(v) => !v && acknowledge()}>
      <AlertDialogContent data-testid="mobile-advisory-modal">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-full bg-[#2400FF]/10 flex items-center justify-center shrink-0">
              <Desktop size={20} weight="duotone" className="text-[#2400FF]" />
            </div>
            <AlertDialogTitle className="font-heading tracking-tight text-lg leading-snug">
              Best experienced on a larger screen
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-sm leading-relaxed text-neutral-700 space-y-2 pt-1">
            <span className="block">
              MyResumeDesign is designed and tuned for desktop and laptop browsers,
              where the live preview, template thumbnails, and two-page pagination
              display at their intended fidelity.
            </span>
            <span className="block">
              You can continue on your phone — every feature is fully functional
              here — but a larger screen makes reviewing layout and typography
              considerably easier.
            </span>
            <span className="inline-flex items-center gap-2 text-xs text-neutral-500 font-mono uppercase tracking-widest pt-1">
              <DeviceMobile size={12} weight="bold" />
              Mobile view · optimised, not preferred
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            onClick={acknowledge}
            className="bg-neutral-950 text-white hover:bg-neutral-800"
            data-testid="mobile-advisory-acknowledge"
          >
            Continue on mobile
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Utility so the parent can check the flag without importing internals.
export function hasAckMobileAdvisory() {
  try {
    return sessionStorage.getItem(SESSION_KEY) === "1";
  } catch (e) {
    return false;
  }
}
